import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { error, failedResponse, getBackendUrl, loaded, loading, LoadState, LoadStateInfo, notStarted } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { SubjectPerformedInfo } from "./model";
import { subjectPerformedInsert } from "./subjectPerformedInsUpdSlice";

export interface SubjectPerformedListState {
    state: LoadStateInfo;
    request?: RetrieveSubjectPerformedListRequest;
    list?: SubjectPerformedInfo[];
    changing: boolean;
    selectedToPresence?: SubjectPerformedInfo;
}

const initialState: SubjectPerformedListState = {
    state: notStarted(),
    changing: false,
};

export interface RetrieveSubjectPerformedListRequest {
    classroomId: string;
}

export const retrieveSubjectPerformedList = createAsyncThunk(
    "retrieveSubjectPerformedList",
    async (param: RetrieveSubjectPerformedListRequest, thunkAPI) => {
        console.debug(`retrieve subject performed list for classroom ${param.classroomId}`);

        const response = await fetch(`${getBackendUrl()}/api/subject-performed/classroom/${param.classroomId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (_param: RetrieveSubjectPerformedListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = (state as any).subjectPerformedList?.state as LoadStateInfo | undefined;
            if (status && (status.state == LoadState.Loading || status.state == LoadState.Failed)) {
                return false;
            }
            return true;
        }
    }
);

export const subjectPerformedListSlice = createSlice({
    name: "subjectPerformedList",
    initialState,
    reducers: {
        check: (state, action: PayloadAction<RetrieveSubjectPerformedListRequest>) => {
            if (`${state.request?.classroomId}` != `${action.payload.classroomId}`) {
                state.state = notStarted();
                state.request = { classroomId: action.payload.classroomId };
                state.list = undefined;
            }
        },
        changing: (state, action: PayloadAction<boolean>) => {
            state.changing = action.payload;
        },
        selectedToPresence: (state, action: PayloadAction<SubjectPerformedInfo | undefined>) => {
            state.selectedToPresence = action.payload;
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSubjectPerformedList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveSubjectPerformedList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => {
                    const startedCompare = (`${a?.startedAt || ""}`).localeCompare(`${b?.startedAt || ""}`);
                    if (startedCompare !== 0) return startedCompare;
                    return (`${a?.id || 0}`).localeCompare(`${b?.id || 0}`);
                });
            })
            .addCase(retrieveSubjectPerformedList.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(subjectPerformedInsert.fulfilled, (state, action) => {
                state.selectedToPresence = action.payload;
            })
            ;
    },
});

export const selectSubjectPerformedList = (state: RootState) => (state as any).subjectPerformedList as SubjectPerformedListState;

export default subjectPerformedListSlice.reducer;
